<script lang="ts">
import ElTableMobile, { ElMobileTableProps, MobileTableFilter, MobileTableFiltersStatus, MobileTableRow } from '@/table/ElMobileTable.vue';
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { DataControls } from './ElServerSideTable.vue';

export type MobileDataControls = Pick<DataControls, 'paginationStatus'> & {
  triggeredEvent: 'paginate' | 'filter' | 'firstLoad' | 'trigger';
  filterStatus: MobileTableFiltersStatus;
};

export type ElServerSideMobileTableProps = Pick<ElMobileTableProps, 'selection' | 'color' | 'preventDefaultItemClick'> & {
  filters?: {
    freeSearch?: Omit<NonNullable<MobileTableFilter['freeSearch']>, 'filterOn'>;
  } & Pick<MobileTableFilter, 'dateRange' | 'multiValue' | 'resetButton'>;
  dataController: (args: MobileDataControls) => Promise<{ data: MobileTableRow[]; totalRows: number }>;
};
</script>

<script setup lang="ts">
const loading = ref(false);
const props = defineProps<ElServerSideMobileTableProps>();
const data = ref<MobileTableRow[]>([]);
const currentPage = ref<number>(1);
const totalItemsCount = ref<number | undefined>(undefined);
const filterStatus = ref<MobileTableFiltersStatus>({
  freeSearch: '',
  fromDate: 0,
  multiSelect: {
    avatar: '',
    mainText: '',
    secondaryRow: '',
    tertiaryRow: '',
    amount: '',
    tags: '',
    time: '',
  },
});
const fetchNextPage = () => {
  loading.value = true;
  props
    .dataController({
      paginationStatus: {
        currentPage: currentPage.value + 1,
      },
      filterStatus: filterStatus.value,
      triggeredEvent: 'paginate',
    })
    .then(response => {
      data.value.push(...response.data);
      totalItemsCount.value = response.totalRows;
    })
    .finally(() => {
      nextTick(() =>
        nextTick(() => {
          loading.value = false;
        }),
      );
      currentPage.value += 1;
    });
};
const fetchFirstPage = (triggeredEvent: 'firstLoad' | 'filter') => {
  loading.value = true;
  currentPage.value = 0;
  data.value = [];
  return props
    .dataController({
      paginationStatus: {
        currentPage: 0,
      },
      filterStatus: filterStatus.value,
      triggeredEvent: triggeredEvent,
    })
    .then(response => {
      data.value = response.data;
      totalItemsCount.value = response.totalRows;
    })
    .finally(() => {
      nextTick(() =>
        nextTick(() => {
          loading.value = false;
        }),
      );
    });
};
const onFiltersUpdated = (filters: MobileTableFiltersStatus) => {
  filterStatus.value = filters;
  fetchFirstPage('filter');
};
// fetch initial data
onMounted(() => {
  fetchFirstPage('firstLoad');
});

// infinite scroll
const onInfiniteScroll = () => {
  if (totalItemsCount.value && data.value.length < totalItemsCount.value) {
    fetchNextPage();
  }
};
const infiniteScrollEl = ref<HTMLDivElement | null>();
const observer = ref<IntersectionObserver | undefined>(undefined);
onMounted(() => {
  observer.value = new IntersectionObserver(
    entry => {
      if (entry.some(e => e.isIntersecting) && !loading.value) {
        onInfiniteScroll();
      }
    },
    {
      threshold: 1.0,
    },
  );
  if (infiniteScrollEl.value) {
    observer.value.observe(infiniteScrollEl.value!);
  }
});
watch(infiniteScrollEl, (newValue, oldValue) => {
  if (oldValue) observer.value!.unobserve(oldValue);
  if (newValue) {
    observer.value!.observe(newValue);
  }
});
onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
    observer.value = undefined;
  }
});

const table = ref<InstanceType<typeof ElTableMobile> | null>(null);

const selectRows = (rowsIds: string[]) => {
  table.value?.selectRows(rowsIds);
};

const unselectAllRows = () => table.value?.unselectAllRows();

const getSelectedRows = () => table.value?.getSelectedRows() ?? [];

const resetAllAndFetch = () => {
  filterStatus.value = {
    freeSearch: '',
    fromDate: 0,
    multiSelect: {
      avatar: '',
      mainText: '',
      secondaryRow: '',
      tertiaryRow: '',
      amount: '',
      tags: '',
      time: '',
    },
  };
  table.value?.unselectAllRows();
  return fetchFirstPage('firstLoad');
};
const triggerDataController = async () => {
  loading.value = true;
  const newData = await props.dataController({
    paginationStatus: {
      currentPage: currentPage.value,
    },
    filterStatus: filterStatus.value,
    triggeredEvent: 'trigger',
  });
  data.value = newData.data;
  totalItemsCount.value = newData.totalRows;
  loading.value = false;
};

const closeItemDetails = () => {
  table.value?.closeItemDetails();
};

defineExpose({
  selectRows,
  unselectAllRows,
  getSelectedRows,
  resetAllAndFetch,
  triggerDataController,
  closeItemDetails,
});

const emits = defineEmits<{
  (event: 'bulk:click', rows: MobileTableRow[]): void;
  (event: 'destructive:click', rows: MobileTableRow[]): void;
  (event: 'selection:exit'): void;
  (event: 'rows-selected', rows: MobileTableRow[]): void;
  (event: 'item:click', row: MobileTableRow): void;
}>();
</script>

<template>
  <ElTableMobile
    ref="table"
    :color="props.color"
    :selection="props.selection"
    :data="data"
    :filters="{
      ...props.filters,
      freeSearch: props.filters?.freeSearch
        ? {
            placeholder: props.filters.freeSearch.placeholder,
            initialValue: props.filters.freeSearch.initialValue,
            filterOn: [],
          }
        : undefined,
      managed: true,
    }"
    :loading="loading"
    :prevent-default-item-click="props.preventDefaultItemClick"
    @rows-selected="v => $emit('rows-selected', v)"
    @filters-updated="onFiltersUpdated"
    @bulk:click="v => $emit('bulk:click', v)"
    @selection:exit="$emit('selection:exit')"
    @destructive:click="v => $emit('destructive:click', v)"
    @item:click="v => $emit('item:click', v)"
  >
    <template #default>
      <slot />
    </template>
    <template #after-list>
      <div ref="infiniteScrollEl" class="h-1 mb-2 shrink-0" />
    </template>
  </ElTableMobile>
</template>
